import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { supabase } from '../supabaseClient';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  z-index: 999;
`;

const PanelContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 680px;
  background: ${props => props.theme.colors.background};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.2);
`;

const PanelHeader = styled.div`
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border-bottom: 1px solid ${props => props.theme.colors.borderSubtle};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.theme.colors.surfaceCard};
`;

const HeaderLeft = styled.div``;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.textLight};
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Subtitle = styled.p`
  font-size: 0.8125rem;
  color: ${props => props.theme.colors.subtextGray};
  margin: 0;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  color: ${props => props.theme.colors.subtextGray};
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.surfaceCard};
    color: ${props => props.theme.colors.textLight};
    border-color: ${props => props.theme.colors.accentPurple};
  }
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.xl};
`;

const PanelFooter = styled.div`
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.borderSubtle};
  background: ${props => props.theme.colors.surfaceCard};
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const InputGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: 0.8125rem;
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 12px;
  font-family: inherit;
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.textLight};
  transition: all 0.2s;

  &::placeholder {
    color: ${props => props.theme.colors.subtextGray};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accentPurple};
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 12px;
  font-family: inherit;
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.textLight};
  resize: vertical;
  min-height: ${props => props.minHeight || '100px'};
  transition: all 0.2s;

  &::placeholder {
    color: ${props => props.theme.colors.subtextGray};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accentPurple};
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }
`;

const Button = styled(motion.button)`
  padding: 12px 24px;
  background: ${props => {
    if (props.variant === 'secondary') return 'transparent';
    if (props.variant === 'danger') return 'rgba(239, 68, 68, 0.1)';
    return props.theme.gradients.primary;
  }};
  color: ${props => {
    if (props.variant === 'secondary') return props.theme.colors.textDark;
    if (props.variant === 'danger') return '#EF4444';
    return 'white';
  }};
  border: 1px solid ${props => {
    if (props.variant === 'secondary') return props.theme.colors.borderSubtle;
    if (props.variant === 'danger') return '#EF4444';
    return 'transparent';
  }};
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: ${props => props.theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  flex: ${props => props.flex || 'initial'};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 12px;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: ${props => props.active ? props.theme.gradients.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.subtextGray};
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.active ? 'white' : props.theme.colors.textLight};
  }
`;

const PreviewCard = styled.div`
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 16px;
  padding: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.md};

  img {
    max-width: 100%;
    border-radius: 12px;
    margin: ${props => props.theme.spacing.md} 0;
  }

  h1, h2, h3 {
    margin-top: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.sm};
    color: ${props => props.theme.colors.textLight};
  }

  h2 { color: ${props => props.theme.colors.accentPurple}; }

  p {
    line-height: 1.7;
    color: ${props => props.theme.colors.textDark};
    margin-bottom: ${props => props.theme.spacing.md};
  }

  ul, ol {
    margin-left: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textDark};
  }

  code {
    background: ${props => props.theme.gradients.subtle};
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 0.875em;
  }

  blockquote {
    border-left: 3px solid ${props => props.theme.colors.accentPurple};
    padding-left: ${props => props.theme.spacing.md};
    margin: ${props => props.theme.spacing.md} 0;
    color: ${props => props.theme.colors.subtextGray};
    font-style: italic;
  }
`;

const ThumbnailPreview = styled.div`
  width: 100%;
  height: 160px;
  background: ${props => props.src ? `url(${props.src})` : props.theme.gradients.subtle};
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  margin-top: ${props => props.theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.subtextGray};
  font-size: 0.875rem;
`;

const WordCount = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.subtextGray};
  text-align: right;
  margin-top: 4px;
`;

const HelperText = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.subtextGray};
  margin-top: 6px;
`;

const ImagePickerSection = styled.div`
  margin-top: ${props => props.theme.spacing.md};
`;

const ImagePickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const SearchImagesButton = styled(motion.button)`
  padding: 8px 16px;
  background: ${props => props.theme.gradients.subtle};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 8px;
  color: ${props => props.theme.colors.textLight};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.theme.colors.accentPurple};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.sm};
`;

const ImageOption = styled.div`
  position: relative;
  aspect-ratio: 16/10;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${props => props.selected ? props.theme.colors.accentPurple : 'transparent'};
  transition: all 0.2s;

  &:hover {
    transform: scale(1.02);
    box-shadow: ${props => props.theme.shadows.card};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageSelectedBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  background: ${props => props.theme.colors.accentPurple};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
`;

const ImageCredit = styled.div`
  font-size: 0.6875rem;
  color: ${props => props.theme.colors.subtextGray};
  text-align: center;
  margin-top: ${props => props.theme.spacing.xs};

  a {
    color: ${props => props.theme.colors.accentPurple};
    text-decoration: none;
  }
`;

const NoImagesMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.subtextGray};
  font-size: 0.875rem;
`;

const AdminPanel = ({ isOpen, onClose, onPublish, session, existingBlog }) => {
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [activeTab, setActiveTab] = useState('edit');
  const [searchingImages, setSearchingImages] = useState(false);
  const [imageOptions, setImageOptions] = useState([]);

  useEffect(() => {
    if (existingBlog) {
      setTopic(existingBlog.title);
      setContent(existingBlog.content);
      setThumbnailUrl(existingBlog.thumbnail_url || '');
    } else {
      setTopic('');
      setContent(null);
      setThumbnailUrl('');
    }
    setActiveTab('edit');
    setImageOptions([]);
  }, [existingBlog, isOpen]);

  const searchImages = async () => {
    if (!topic) return;
    setSearchingImages(true);

    try {
      const response = await fetch('https://sledaqnrsijkifnfelnp.supabase.co/functions/v1/clever-api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          topic,
          action: 'search-images'
        })
      });

      const data = await response.json();
      console.log('Image search results:', data);

      if (data?.images) {
        setImageOptions(data.images);
      } else {
        console.error('No images found:', data);
        setImageOptions([]);
      }
    } catch (error) {
      console.error('Error searching images:', error);
      setImageOptions([]);
    } finally {
      setSearchingImages(false);
    }
  };

  const selectImage = (image) => {
    setThumbnailUrl(image.url);
  };

  const generateBlog = async () => {
    if (!topic) return;
    setLoading(true);

    try {
      const response = await fetch('https://sledaqnrsijkifnfelnp.supabase.co/functions/v1/clever-api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ topic })
      });

      const data = await response.json();
      console.log('API Response:', data);

      if (data?.content) {
        setContent(data.content);

        // Try to extract thumbnail from response or content
        let extractedThumb = data.thumbnailUrl;

        if (!extractedThumb) {
          // Try markdown image syntax: ![alt](url)
          const mdMatch = data.content.match(/!\[[^\]]*\]\(([^)]+)\)/);
          if (mdMatch) {
            extractedThumb = mdMatch[1];
          }
        }

        if (!extractedThumb) {
          // Try HTML img tag: <img src="url">
          const htmlMatch = data.content.match(/<img[^>]+src=["']([^"']+)["']/i);
          if (htmlMatch) {
            extractedThumb = htmlMatch[1];
          }
        }

        if (!extractedThumb) {
          // Try plain URL ending in image extension
          const urlMatch = data.content.match(/(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp))/i);
          if (urlMatch) {
            extractedThumb = urlMatch[1];
          }
        }

        if (extractedThumb) {
          console.log('Extracted thumbnail:', extractedThumb);
          setThumbnailUrl(extractedThumb);
        }
      } else if (data?.error) {
        console.error('Edge function error:', data.error);
        setContent(`# Error\n\nFailed to generate blog: ${data.error}`);
      } else {
        console.error('Unexpected response:', data);
        setContent("# Error\n\nNo content generated. Please try again.");
      }

    } catch (error) {
      console.error('Error generating blog:', error);
      setContent(`# Error\n\nFailed to generate blog. Please try again.\n\nError details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Generate URL-friendly slug from title
  const generateSlug = (title) => {
    return (title || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 100);
  };

  const handlePublish = async (status = 'published') => {
    if (!content) return;

    setSaving(true);
    try {
      const slug = existingBlog?.slug || generateSlug(topic) + '-' + Date.now();

      const blogData = {
        title: topic,
        slug: slug,
        content: content,
        thumbnail_url: thumbnailUrl,
        status: status,
        author_id: session?.user?.id,
        published_at: status === 'published' ? new Date() : null,
      };

      let error;

      if (existingBlog) {
        const { error: updateError } = await supabase
          .from('blogs')
          .update(blogData)
          .eq('id', existingBlog.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('blogs')
          .insert([blogData]);
        error = insertError;
      }

      if (error) throw error;

      if (onPublish) onPublish();
      if (!existingBlog) {
        setTopic('');
        setContent(null);
        setThumbnailUrl('');
      }
      onClose();
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Failed to save blog: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingBlog || !window.confirm('Are you sure you want to delete this blog?')) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('blogs')
        .delete()
        .eq('id', existingBlog.id);

      if (error) throw error;
      if (onPublish) onPublish();
      onClose();
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Failed to delete blog');
    } finally {
      setSaving(false);
    }
  };

  const wordCount = content?.split(/\s+/).filter(Boolean).length || 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <PanelContainer
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <PanelHeader>
              <HeaderLeft>
                <Title>
                  ✨ {existingBlog ? 'Edit Blog' : 'Create Blog'}
                </Title>
                <Subtitle>
                  {existingBlog ? 'Make changes to your content' : 'Generate AI-powered content or write your own'}
                </Subtitle>
              </HeaderLeft>
              <CloseButton onClick={onClose}>×</CloseButton>
            </PanelHeader>

            <PanelBody>
              {!content ? (
                <>
                  <InputGroup>
                    <Label>Topic / Title</Label>
                    <TextArea
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder="What should I write about? E.g., The future of AI in manufacturing..."
                      minHeight="100px"
                    />
                    <HelperText>Describe your topic in detail for better AI-generated content</HelperText>
                  </InputGroup>

                  <Button
                    onClick={generateBlog}
                    disabled={!topic || loading}
                    whileTap={{ scale: 0.98 }}
                    style={{ width: '100%', marginBottom: '16px' }}
                  >
                    {loading ? (
                      <>
                        <Spinner />
                        Generating with AI...
                      </>
                    ) : (
                      <>
                        ✨ Generate with AI
                      </>
                    )}
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() => setContent('')}
                    whileTap={{ scale: 0.98 }}
                    style={{ width: '100%' }}
                  >
                    📝 Write Manually
                  </Button>
                </>
              ) : (
                <>
                  <TabRow>
                    <Tab active={activeTab === 'edit'} onClick={() => setActiveTab('edit')}>
                      ✎ Edit
                    </Tab>
                    <Tab active={activeTab === 'preview'} onClick={() => setActiveTab('preview')}>
                      👁 Preview
                    </Tab>
                  </TabRow>

                  {activeTab === 'edit' ? (
                    <>
                      <InputGroup>
                        <Label>Title</Label>
                        <Input
                          value={topic}
                          onChange={(e) => setTopic(e.target.value)}
                          placeholder="Blog title..."
                        />
                      </InputGroup>

                      <InputGroup>
                        <Label>Thumbnail</Label>
                        <Input
                          value={thumbnailUrl}
                          onChange={(e) => setThumbnailUrl(e.target.value)}
                          placeholder="https://example.com/image.jpg or search below"
                        />
                        {thumbnailUrl && <ThumbnailPreview src={thumbnailUrl} />}
                        {!thumbnailUrl && <ThumbnailPreview>No thumbnail selected</ThumbnailPreview>}

                        <ImagePickerSection>
                          <ImagePickerHeader>
                            <Label style={{ margin: 0 }}>Or search for images</Label>
                            <SearchImagesButton
                              onClick={searchImages}
                              disabled={searchingImages || !topic}
                              whileTap={{ scale: 0.98 }}
                            >
                              {searchingImages ? '🔄 Searching...' : '🔍 Search Images'}
                            </SearchImagesButton>
                          </ImagePickerHeader>

                          {imageOptions.length > 0 && (
                            <>
                              <ImageGrid>
                                {imageOptions.map((image) => (
                                  <ImageOption
                                    key={image.id}
                                    selected={thumbnailUrl === image.url}
                                    onClick={() => selectImage(image)}
                                  >
                                    <img src={image.thumb} alt={image.alt} />
                                    {thumbnailUrl === image.url && (
                                      <ImageSelectedBadge>✓</ImageSelectedBadge>
                                    )}
                                  </ImageOption>
                                ))}
                              </ImageGrid>
                              <ImageCredit>
                                Photos by <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer">Unsplash</a>
                              </ImageCredit>
                            </>
                          )}

                          {imageOptions.length === 0 && searchingImages === false && (
                            <NoImagesMessage>
                              Click "Search Images" to find photos for your blog
                            </NoImagesMessage>
                          )}
                        </ImagePickerSection>
                      </InputGroup>

                      <InputGroup>
                        <Label>Content (Markdown)</Label>
                        <TextArea
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                          minHeight="350px"
                          style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.875rem' }}
                        />
                        <WordCount>{wordCount} words</WordCount>
                      </InputGroup>
                    </>
                  ) : (
                    <PreviewCard>
                      {thumbnailUrl && (
                        <img
                          src={thumbnailUrl}
                          alt="Thumbnail"
                          style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                        />
                      )}
                      <h1 style={{ marginTop: thumbnailUrl ? '1rem' : 0 }}>{topic || 'Untitled'}</h1>
                      <ReactMarkdown>{content}</ReactMarkdown>
                    </PreviewCard>
                  )}
                </>
              )}
            </PanelBody>

            {content !== null && (
              <PanelFooter>
                <Button
                  onClick={() => handlePublish('published')}
                  whileTap={{ scale: 0.98 }}
                  disabled={saving || !topic}
                  flex="1"
                >
                  {saving ? <Spinner /> : '🚀'} {existingBlog ? 'Update' : 'Publish'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handlePublish('draft')}
                  whileTap={{ scale: 0.98 }}
                  disabled={saving}
                >
                  Save Draft
                </Button>
                {existingBlog ? (
                  <Button
                    variant="danger"
                    onClick={handleDelete}
                    whileTap={{ scale: 0.98 }}
                    disabled={saving}
                  >
                    🗑 Delete
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => setContent(null)}
                    whileTap={{ scale: 0.98 }}
                    disabled={saving}
                  >
                    ← Back
                  </Button>
                )}
              </PanelFooter>
            )}
          </PanelContainer>
        </>
      )}
    </AnimatePresence>
  );
};

export default AdminPanel;
